import { useState, useEffect } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { Button } from "@/components/uiDir/button";
import { Card, CardContent } from "@/components/uiDir/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/uiDir/avatar";
import { Badge } from "@/components/uiDir/badge";
import { 
  Mail, 
  MapPin, 
  Github, 
  Linkedin, 
  Globe, 
  Download,
  ArrowLeft,
  ExternalLink,
  Heart,
  Share2,
  Loader2,
  Phone,
  Briefcase,
  Calendar,
  Eye,
  Users,
  FolderOpen,
  Play,
  Image as ImageIcon,
  Code,
  Palette,
  Gamepad2,
  Film,
  Box,
  Link2,
  X,
  ChevronLeft,
  ChevronRight,
  Facebook,
  Instagram,
  Twitter,
  Dribbble,
  Youtube,
  FileText,
  MessageCircle,
  Send,
  Search,
  Plus,
  Edit
} from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/lib/supabaseClient";
import { User } from "@supabase/supabase-js";
import type { Project, ProjectMedia } from "@/lib/models/publicprofileModels";
import type { Profile, SocialLink, Skill } from "@/lib/models/publicprofileModels";

const PublicProfile = () => {
  const { username } = useParams<{ username: string }>();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [socialLinks, setSocialLinks] = useState<SocialLink[]>([]);
  const [skills, setSkills] = useState<Skill[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>("all");
  const [activeTag, setActiveTag] = useState<string>("all");
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [currentUser, setCurrentUser] = useState<User>(null);
  const location = useLocation();

    useEffect(() => {
      const getUser = async () => {
        const { data, error } = await supabase.auth.getUser();
        if (!error && data?.user) setCurrentUser(data.user);
      };
      getUser();
    }, []);


  useEffect(() => {
    if (username) {
      fetchProfileData();
    }

    const projectIdFromHash = location.hash.substring(1);
    if (projectIdFromHash && projects.length > 0) {
      const projectToOpen = projects.find(p => p.id === projectIdFromHash);
      if (projectToOpen) {
        setSelectedProject(projectToOpen);
        setCurrentMediaIndex(0);
      }
    }

  }, [username]);


  const isUuid = (value: string) =>
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);

  const fetchProfileData = async () => {
     try {
       setIsLoading(true);
       setError(null);
 
       const { data, error } = await supabase.rpc('get_public_profile_data', {
         profile_username: username,
       });
 
       if (error) throw error;
       if (!data) throw new Error('Profile not found');
 
       const { profile, social_links, skills, projects: projectsWithDetails } = data;
 
       setProfile(profile);
       setSocialLinks(social_links || []);
       setSkills(skills || []);
       setProjects(projectsWithDetails || []);
 
       // Check hash after projects are loaded
       const projectIdFromHash = window.location.hash.substring(1);
       if (projectIdFromHash && projectsWithDetails) {
         const projectToOpen = projectsWithDetails.find(p => p.id === projectIdFromHash);
         if (projectToOpen) {
           setSelectedProject(projectToOpen);
           setCurrentMediaIndex(0);
         }
       }
     } catch (err) {
       console.error('Error fetching profile:', err);
       setError(err.message || 'Failed to load profile');
       toast.error('Failed to load profile');
     } finally {
       setIsLoading(false);
     }
   };
 
   const handleShare = () => {
     navigator.clipboard.writeText(window.location.href);
     toast.success("Portfolio link copied to clipboard!");
   };
 
   const handleDownloadResume = () => {
     if (profile?.resume_url) {
       window.open(profile.resume_url, '_blank');
       toast.success("Resume download started");
     }
   };
 
   const handleContactScroll = () => {
     document.getElementById('connect-card')?.scrollIntoView({
       behavior: 'smooth'
     });
   };
 
   const getSocialIcon = (platform: string) => {
     switch (platform.toLowerCase()) {
       case 'github': return <Github className="h-5 w-5" />;
       case 'linkedin': return <Linkedin className="h-5 w-5" />;
       case 'facebook': return <Facebook className="h-5 w-5" />;
       case 'instagram': return <Instagram className="h-5 w-5" />;
       case 'twitter': return <Twitter className="h-5 w-5" />;
       case 'dribbble': return <Dribbble className="h-5 w-5" />;
       case 'youtube': return <Youtube className="h-5 w-5" />;
       case 'portfolio': return <Globe className="h-5 w-5" />;
       case 'medium': return <FileText className="h-5 w-5" />;
       default: return <Globe className="h-5 w-5" />;
     }
   };
 
   const getProjectIcon = (category: string) => {
     const cat = category.toLowerCase();
     if (cat.includes('video') || cat.includes('film')) return <Film className="h-4 w-4" />;
     if (cat.includes('game')) return <Gamepad2 className="h-4 w-4" />;
     if (cat.includes('web') || cat.includes('code')) return <Code className="h-4 w-4" />;
     if (cat.includes('design') || cat.includes('graphic')) return <Palette className="h-4 w-4" />;
     if (cat.includes('3d') || cat.includes('ar')) return <Box className="h-4 w-4" />;
     return <FolderOpen className="h-4 w-4" />;
   };
 
   const getSkillLevel = (level: string): number => {
     const levels: Record<string, number> = {
       beginner: 1,
       intermediate: 2,
       advanced: 3,
       expert: 4
     };
     return levels[level.toLowerCase()] || 2;
   };
 
   const getCoverMedia = (project: Project): ProjectMedia | null => {
     return project.media.find(m => m.is_cover) || project.media[0] || null;
   };
 
   const categories = ['all', ...Array.from(new Set(projects.map(p => p.category)))];
   const allTags = Array.from(new Set(projects.flatMap(p => p.tags)));
 
   const filteredProjects = projects.filter(project => {
     const categoryMatch = activeCategory === 'all' || project.category === activeCategory;
     const tagMatch = activeTag === 'all' || project.tags.includes(activeTag);
     return categoryMatch && tagMatch;
   });
 
   const featuredProjects = projects.filter(p => p.featured).slice(0, 3);
   const skillsByCategory = skills.reduce((acc, skill) => {
     if (!acc[skill.category]) acc[skill.category] = [];
     acc[skill.category].push(skill);
     return acc;
   }, {} as Record<string, Skill[]>);
 
   const ProjectModal = ({ project, onClose }: { project: Project; onClose: () => void }) => {
     const media = project.media[currentMediaIndex];
 
     return (
       <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={onClose}>
         <div className="bg-white rounded-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
           <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between z-10">
             <h3 className="text-2xl font-bold text-gray-900">{project.title}</h3>
             <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
               <X className="h-6 w-6" />
             </button>
           </div>
 
           <div className="p-6">
             {/* Media Gallery */}
             {project.media.length > 0 && (
               <div className="mb-6">
                 <div className="relative bg-gray-900 rounded-xl overflow-hidden aspect-video">
                   {media?.file_type === 'video' ? (
                     <video 
                       src={media.file_url} 
                       controls 
                       className="w-full h-full object-contain"
                     />
                   ) : (
                     <img 
                       src={media?.file_url} 
                       alt={project.title}
                       className="w-full h-full object-contain"
                     />
                   )}
                   
                   {project.media.length > 1 && (
                     <>
                       <button
                         onClick={() => setCurrentMediaIndex((currentMediaIndex - 1 + project.media.length) % project.media.length)}
                         className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg"
                       >
                         <ChevronLeft className="h-6 w-6" />
                       </button>
                       <button
                         onClick={() => setCurrentMediaIndex((currentMediaIndex + 1) % project.media.length)}
                         className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg"
                       >
                         <ChevronRight className="h-6 w-6" />
                       </button>
                     </>
                   )}
                 </div>
 
                 {project.media.length > 1 && (
                   <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
                     {project.media.map((m, i) => (
                       <button
                         key={m.id}
                         onClick={() => setCurrentMediaIndex(i)}
                         className={`relative flex-shrink-0 w-24 h-24 rounded-lg overflow-hidden ${
                           i === currentMediaIndex ? 'ring-2 ring-blue-500' : ''
                         }`}
                       >
                         <img 
                           src={m.thumbnail_url || m.file_url} 
                           alt=""
                           className="w-full h-full object-cover"
                         />
                         {m.file_type === 'video' && (
                           <Play className="absolute inset-0 m-auto h-6 w-6 text-white" />
                         )}
                       </button>
                     ))}
                   </div>
                 )}
               </div>
             )}
 
             {/* Project Details */}
             <div className="space-y-6">
               <div>
                 <h4 className="text-sm font-semibold text-gray-500 mb-2">DESCRIPTION</h4>
                 <p className="text-gray-700 leading-relaxed">{project.description}</p>
               </div>
 
               {/* Links */}
               <div className="flex flex-wrap gap-3">
                 {project.live_url && (
                   <a href={project.live_url} target="_blank" rel="noopener noreferrer">
                     <Button variant="default" className="bg-blue-600 hover:bg-blue-700">
                       <ExternalLink className="h-4 w-4 mr-2" />
                       View Live
                     </Button>
                   </a>
                 )}
                 {project.github_url && (
                   <a href={project.github_url} target="_blank" rel="noopener noreferrer">
                     <Button variant="outline">
                       <Github className="h-4 w-4 mr-2" />
                       GitHub
                     </Button>
                   </a>
                 )}
                 {project.youtube_url && (
                   <a href={project.youtube_url} target="_blank" rel="noopener noreferrer">
                     <Button variant="outline">
                       <Youtube className="h-4 w-4 mr-2" />
                       YouTube
                     </Button>
                   </a>
                 )}
                 {project.ar_model_url && (
                   <a href={project.ar_model_url} target="_blank" rel="noopener noreferrer">
                     <Button variant="outline">
                       <Box className="h-4 w-4 mr-2" />
                       View in AR
                     </Button>
                   </a>
                 )}
                 {project.case_study_url && (
                   <a href={project.case_study_url} target="_blank" rel="noopener noreferrer">
                     <Button variant="outline">
                       <FileText className="h-4 w-4 mr-2" />
                       Case Study
                     </Button>
                   </a>
                 )}
               </div>
 
               {/* Tags */}
               {project.tags.length > 0 && (
                 <div>
                   <h4 className="text-sm font-semibold text-gray-500 mb-2">TAGS</h4>
                   <div className="flex flex-wrap gap-2">
                     {project.tags.map((tag, i) => (
                       <Badge key={i} variant="secondary" className="bg-blue-50 text-blue-700">
                         {tag}
                       </Badge>
                     ))}
                   </div>
                 </div>
               )}
 
               {/* Collaborators */}
               {project.collaborators.length > 0 && (
                 <div>
                   <h4 className="text-sm font-semibold text-gray-500 mb-3">COLLABORATORS</h4>
                   <div className="space-y-2">
                     {project.collaborators.map((collab, i) => (
                       <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                         <Avatar className="h-10 w-10">
                           <AvatarFallback>
                             {collab.collaborator_name.split(' ').map(n => n[0]).join('')}
                           </AvatarFallback>
                         </Avatar>
                         <div className="flex-1">
                           {collab.collaborator_id ? (
                             <Link to={`/profile/${collab.collaborator_id}`} className="font-medium text-blue-600 hover:underline">
                               {collab.collaborator_name}
                             </Link>
                           ) : (
                             <p className="font-medium text-gray-900">{collab.collaborator_name}</p>
                           )}
                           <p className="text-sm text-gray-500">{collab.role}</p>
                         </div>
                       </div>
                     ))}
                   </div>
                 </div>
               )}
 
               {/* Stats */}
               {/* <div className="flex items-center gap-6 text-sm text-gray-600">
                 <div className="flex items-center gap-2">
                   <Heart className="h-4 w-4 text-red-500" />
                   {project.likes_count} likes
                 </div>
                 <div className="flex items-center gap-2">
                   <Eye className="h-4 w-4" />
                   {project.views_count} views
                 </div>
             
               </div> */}
             </div>
           </div>
         </div>
       </div>
     );
   };
 
   if (isLoading) {
     return (
       <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center">
         <div className="text-center">
           <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
           <p className="text-gray-600">Loading portfolio...</p>
         </div>
       </div>
     );
   }
 
   if (error || !profile) {
     return (
       <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center">
         <div className="text-center">
           <h2 className="text-2xl font-bold mb-2">Profile Not Found</h2>
           <p className="text-gray-600 mb-4">{error || 'The profile you are looking for does not exist.'}</p>
           <Link to="/explore">
             <Button>Back to Explore</Button>
           </Link>
         </div>
       </div>
     );
   }
 
   return (
     <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
       {/* Header */}
       <header
         className="bg-white/80 border-b sticky z-40 backdrop-blur-lg shadow-sm"
         style={{ top: "var(--beta-banner-height)" }}
       >
         <div className="container mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
           <Link to="/explore" className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 transition-colors">
             <ArrowLeft className="h-4 w-4" />
             Back to Explore
           </Link>
           <div className="flex items-center">
         {currentUser?.id === profile.id && (
            <Link to="/dashboard/profile">
             <Button
               variant="outline"
               size="sm"
               className="border-green-600 text-green-600 hover:bg-green-50 mr-4"
             >
               <Briefcase className="h-4 w-4 mr-2" />
               Edit Profile
             </Button>
           </Link>
         )}
           <Button variant="outline" size="sm" onClick={handleShare} className="border-blue-600 text-blue-600 hover:bg-blue-50">
             <Share2 className="h-4 w-4 mr-2" />
             Share
           </Button>
           </div>
         </div>
       </header>
 
       {/* Cover Image */}
       {profile.cover_image_url && (
         <div className="relative h-48 sm:h-64 lg:h-80 w-full overflow-hidden">
           <img 
             src={profile.cover_image_url} 
             alt="Cover" 
             className="w-full h-full object-cover"
           />
           <div className="absolute inset-0 bg-gradient-to-b from-transparent to-white/90" />
         </div>
       )}
 
       <div className="container mx-auto px-4 sm:px-6 relative z-10 - mt-10">
         {/* Profile Header */}
          <Card className="max-w-6xl mx-auto mb-8 sm:mb-10 shadow-xl border border-primary-100 rounded-2xl bg-white">
              <CardContent className="p-6 sm:p-8 lg:p-10">
         <div className="flex flex-col items-center text-center gap-6 sm:gap-7">
           {/* Avatar */}
           <div className="relative inline-block">
             <Avatar
               className={`h-32 w-32 sm:h-36 sm:w-36 lg:h-44 lg:w-44 border-[3px] shadow-lg transition-all duration-200 ${
                 profile.available_for_work 
                   ? "border-green-500" 
                   : "border-primary-500"
               }`}
             >
               <AvatarImage
                 src={
                   profile.avatar_url ||
                   `https://api.dicebear.com/7.x/avataaars/svg?seed=${profile.username}`
                 }
                 className="object-cover"
               />
               <AvatarFallback className="text-xl font-semibold">
                 {profile.full_name
                   .split(" ")
                   .map((n) => n[0])
                   .join("")}
               </AvatarFallback>
             </Avatar>

             {profile.available_for_work && (
               <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2">
                 <Badge className="bg-green-500 text-white hover:bg-green-600 border-0 px-3 py-1 shadow-sm">
                   Available for Work
                 </Badge>
               </div>
             )}
           </div>

           {/* Info */}
           <div className="flex flex-col items-center text-center gap-4 w-full max-w-3xl">
             {/* Name and username */}
             <div className="space-y-1">
               <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">
                 {profile.full_name}
               </h1>
               <p className="text-primary-600 text-base font-medium">
                 @{profile.username}
               </p>
             </div>

             {/* Program and title */}
             <div className="flex flex-wrap items-center justify-center gap-2.5">
               <Badge className="bg-primary/10 text-primary border border-primary/20 px-3 py-1">
                 {profile.program}
               </Badge>
               
               {profile.title && (
                 <span className="text-lg font-medium text-gray-800">
                   {profile.title}
                 </span>
               )}
             </div>

             {/* Location */}
             {profile.location && (
               <div className="flex items-center justify-center gap-1.5 text-gray-600">
                 <MapPin className="h-4 w-4 text-primary-500" />
                 <span className="text-sm font-medium">{profile.location}</span>
               </div>
             )}

             {/* Bio */}
             {profile.bio && (
               <p className="text-gray-700 text-base leading-relaxed max-w-2xl mt-1">
                 {profile.bio}
               </p>
             )}

             {/* Action Buttons */}
             <div className="flex flex-wrap justify-center gap-3 mt-3">
               <Button 
                 onClick={handleContactScroll} 
                 className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-sm hover:shadow-md transition-shadow w-full sm:w-auto"
               >
                 <Mail className="h-4 w-4 mr-2" />
                 Contact Me
               </Button>

               {profile.resume_url && (
                 <Button
                   variant="outline"
                   className="border-primary text-primary hover:bg-primary hover:text-primary-foreground shadow-sm hover:shadow-md transition-shadow w-full sm:w-auto"
                 >
                   <Download className="h-4 w-4 mr-2" />
                   Download Resume
                 </Button>
               )}
             </div>
           </div>
         </div>
       </CardContent>
         </Card>
         {/* About Section */}
         {profile.about && (
           <Card className="max-w-6xl mx-auto mb-12 shadow-lg border-0 rounded-2xl">
             <CardContent className="p-8">
               <h2 className="text-2xl font-bold mb-4 text-gray-900">About Me</h2>
               <p className="text-gray-700 leading-relaxed whitespace-pre-line">{profile.about}</p>
             </CardContent>
           </Card>
         )}
 
         
         {/* Skills Section */}
         {/* {skills.length > 0 && (
           <div className="max-w-4xl mx-auto mb-10">
             <h2 className="text-2xl font-bold mb-4 text-gray-900">Skills & Tools</h2>
             <Card className="shadow-lg border border-primary-100 rounded-2xl">
               <CardContent className="p-6">
                 <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
                   {skills.map((skill, index) => {
                     const level = getSkillLevel(skill.level);
                     return (
                       <div key={index} className="space-y-2">
                         <div className="flex items-center justify-between">
                           <span className="font-medium text-gray-800">{skill.name}</span>
                           <span className="text-sm text-gray-500">{level}/4</span>
                         </div>
                         <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                           <div
                             className="h-full bg-primary-500 transition-all rounded-full"
                             style={{ width: `${(level / 4) * 100}%` }}
                           />
                         </div>
                       </div>
                     );
                   })}
                 </div>
               </CardContent>
             </Card>
           </div>
         )} */}
 
 
         {/* Featured Projects */}
         {featuredProjects.length > 0 && (
           <div className="max-w-5xl mx-auto mb-12">
             <h2 className="text-2xl font-bold mb-6 text-gray-900">Featured Work</h2>
             <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
               {featuredProjects.map(project => {
                 const coverMedia = getCoverMedia(project);
                 return (
                   <Card 
                     key={project.id}
                     className="group cursor-pointer hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 overflow-hidden border-0 rounded-xl"
                     onClick={() => {
                       setSelectedProject(project);
                       setCurrentMediaIndex(0);
                     }}
                   >
                     <div className="relative h-48 bg-gray-900 overflow-hidden">
                       {coverMedia && (
                         <>
                           {coverMedia.file_type === 'video' ? (
                             <video src={coverMedia.file_url} className="w-full h-full object-cover" />
                           ) : (
                             <img src={coverMedia.file_url} alt={project.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300" />
                           )}
                         </>
                       )}
                       <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                       <Badge className="absolute top-3 right-3 bg-yellow-500 text-white border-0">
                         Featured
                       </Badge>
                     </div>
                     <CardContent className="p-4">
                       <h3 className="font-semibold text-lg mb-2 text-gray-900">{project.title}</h3>
                       <p className="text-sm text-gray-600 line-clamp-2">{project.description}</p>
                       {/* <div className="flex items-center justify-between mt-3 text-sm text-gray-500">
                         <div className="flex items-center gap-1">
                           <Heart className="h-4 w-4 text-red-500" />
                           {project.likes_count}
                         </div>
                         <div className="flex items-center gap-1">
                           <Eye className="h-4 w-4" />
                           {project.views_count}
                         </div>
                       </div> */}
                     </CardContent>
                   </Card>
                 );
               })}
             </div>
           </div>
         )}
 
         {/* Skills Section */}
         {Object.keys(skillsByCategory).length > 0 && (
           <Card className="max-w-5xl mx-auto mb-12 shadow-lg border-0 rounded-2xl">
             <CardContent className="p-8">
               <h2 className="text-2xl font-bold mb-6 text-gray-900">Skills & Expertise</h2>
               <div className="space-y-6">
                 {Object.entries(skillsByCategory).map(([category, categorySkills]) => (
                   <div key={category}>
                     <h3 className="text-sm font-semibold text-gray-500 mb-3 uppercase">{category}</h3>
                     <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                       {categorySkills.map((skill, index) => {
                         const level = getSkillLevel(skill.level);
                         return (
                           <div key={index} className="space-y-2">
                             <div className="flex items-center justify-between">
                               <span className="font-medium text-gray-800">{skill.name}</span>
                               <span className="text-sm text-gray-500 capitalize">{skill.level}</span>
                             </div>
                             <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                               <div
                                 className="h-full bg-blue-600 transition-all rounded-full"
                                 style={{ width: `${(level / 4) * 100}%` }}
                               />
                             </div>
                           </div>
                         );
                       })}
                     </div>
                   </div>
                 ))}
               </div>
             </CardContent>
           </Card>
         )}
 
         {/* Projects Section */}
         {projects.length > 0 && (
           <div className="max-w-6xl mx-auto mb-12">
             <h2 className="text-2xl font-bold mb-6 text-gray-900">All Projects</h2>
 
             {/* Category Filter */}
             <div className="flex flex-wrap gap-2 mb-4">
               {categories.map(cat => (
                 <Badge
                   key={cat}
                   onClick={() => setActiveCategory(cat)}
                   className={`cursor-pointer px-4 py-2 ${
                     activeCategory === cat
                       ? 'bg-blue-600 text-white hover:bg-blue-700'
                       : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                   }`}
                 >
                   {cat === 'all' ? 'All' : cat}
                 </Badge>
               ))}
             </div>
 
             {/* Tag Filter */}
             {allTags.length > 0 && (
               <div className="flex flex-wrap gap-2 mb-6">
                 <Badge
                   onClick={() => setActiveTag('all')}
                   className={`cursor-pointer ${
                     activeTag === 'all'
                       ? 'bg-blue-100 text-blue-700'
                       : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                   }`}
                 >
                   All Tags
                 </Badge>
                 {allTags.map(tag => (
                   <Badge
                     key={tag}
                     onClick={() => setActiveTag(tag)}
                     className={`cursor-pointer ${
                       activeTag === tag
                         ? 'bg-blue-100 text-blue-700'
                         : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                     }`}
                   >
                     {tag}
                   </Badge>
                 ))}
               </div>
             )}
 
             {/* Projects Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredProjects.map(project => {
                const coverMedia = getCoverMedia(project);
                return (
                  <Card
                    key={project.id}
                    className="group cursor-pointer hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden border-0 rounded-xl"
                    onClick={() => {
                      setSelectedProject(project);
                      setCurrentMediaIndex(0);
                    }}
                  >
                    <div className="relative h-56 bg-gray-900 overflow-hidden">
                      {coverMedia ? (
                        <>
                          {coverMedia.file_type === 'video' ? (
                            <>
                              <video src={coverMedia.file_url} className="w-full h-full object-cover" />
                              <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                                <Play className="h-12 w-12 text-white" />
                              </div>
                            </>
                          ) : (
                            <img
                              src={coverMedia.file_url}
                              alt={project.title}
                              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                            />
                          )}
                        </>
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-500 to-purple-600">
                          {getProjectIcon(project.category)}
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            
                      {/* Project Type Badge */}
                      <Badge className="absolute top-3 left-3 bg-white/90 text-gray-900 border-0">
                        <span className="mr-1">{getProjectIcon(project.category)}</span>
                        {project.category}
                      </Badge>
            
                      {/* Edit button for profile owner */}
                      {currentUser?.id === profile.id && (
                        <Link
                          to={`/dashboard/projects/edit/${project.id}`}
                          onClick={(e) => e.stopPropagation()} // Prevent modal from opening
                          className="absolute top-3 right-3 z-10"
                        >
                          <Button
                            variant="secondary"
                            size="sm"
                            className="h-8 w-8 p-0"
                          >
                            <Edit className="h-4 w-4" />
                            <span className="sr-only">Edit Project</span>
                          </Button>
                        </Link>
                      )}
            
                      {project.featured && (
                        <Badge className="absolute top-3 right-3 bg-yellow-500 text-white border-0">
                          Featured
                        </Badge>
                      )}
            
                      {/* Media Count */}
                      {project.media.length > 1 && (
                        <div className="absolute bottom-3 right-3 bg-black/70 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                          <ImageIcon className="h-3 w-3" />
                          {project.media.length}
                        </div>
                      )}
                    </div>
            
                    <CardContent className="p-5">
                      <h3 className="font-semibold text-lg mb-2 text-gray-900 line-clamp-1">
                        {project.title}
                      </h3>
                      <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                        {project.description}
                      </p>
            
                      {/* Tags */}
                      {project.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-3">
                          {project.tags.slice(0, 3).map((tag, i) => (
                            <Badge key={i} variant="secondary" className="text-xs bg-blue-50 text-blue-700">
                              {tag}
                            </Badge>
                          ))}
                          {project.tags.length > 3 && (
                            <Badge variant="secondary" className="text-xs bg-gray-100 text-gray-600">
                              +{project.tags.length - 3}
                            </Badge>
                          )}
                        </div>
                      )}
            
                      {/* Collaborators Preview */}
                      {project.collaborators.length > 0 && (
                        <div className="flex items-center gap-2 mb-3 text-sm text-gray-600">
                          <Users className="h-4 w-4" />
                          <span>
                            {project.collaborators.length} collaborator{project.collaborators.length > 1 ? 's' : ''}
                          </span>
                        </div>
                      )}
            
                      {/* Stats & Links */}
                      <div className="flex items-center justify-between pt-3 border-t">
                        <div className="flex items-center gap-3 text-sm text-gray-500">
                          <div className="flex items-center gap-1">
                            <Eye className="h-4 w-4" />
                            {project.views_count}
                          </div>
                        </div>
            
                        <div className="flex items-center gap-2">
                          {project.live_url && <ExternalLink className="h-4 w-4 text-blue-600" />}
                          {project.github_url && <Github className="h-4 w-4 text-gray-700" />}
                          {project.youtube_url && <Youtube className="h-4 w-4 text-red-600" />}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            
              {/* Add Project Card - Only show if currentUser owns this profile */}
              {currentUser?.id === profile.id && (
                <Link
                  to="/dashboard/projects/new"
                  className="flex flex-col items-center justify-center cursor-pointer border-2 border-dashed border-gray-300 rounded-xl p-8 text-gray-400 hover:border-blue-500 hover:bg-blue-50 hover:text-blue-600 transition-all"
                >
                  <Card className="flex flex-col items-center justify-center border-0 shadow-none">
                    <div className="flex items-center justify-center w-16 h-16 rounded-full border-2 border-gray-300 mb-4 group-hover:border-blue-500 group-hover:text-blue-600">
                      <Plus className="w-8 h-8" />
                    </div>
                    <span className="font-semibold text-lg select-none">Add Project</span>
                  </Card>
                </Link>
              )}
            
            </div>
             {filteredProjects.length === 0 && (
               <div className="text-center py-12">
                 <FolderOpen className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                 <p className="text-gray-500">No projects found with selected filters</p>
               </div>
             )}
           </div>
         )}
 
         {/* Connect Section */}
        {(socialLinks.length > 0 || currentUser?.id === profile.id) && (
           <Card id="connect-card" className="connect-card max-w-3xl mx-auto mb-12 shadow-lg border-0 rounded-2xl">
             <CardContent className="p-8">
               <h2 className="text-2xl font-bold text-center mb-6 text-gray-900">
                 Let's Connect
               </h2>
 
               {/* Social Links Grid */}
               {socialLinks.length > 0 ? (
                 <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                   {socialLinks.map((social, index) => (
                     <a
                       key={index}
                       href={social.url}
                       target="_blank"
                       rel="noopener noreferrer"
                       className="flex items-center gap-4 p-4 border border-gray-200 rounded-xl hover:bg-blue-50 hover:border-blue-300 transition-all group"
                     >
                       <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition-colors">
                         {getSocialIcon(social.platform)}
                       </div>
                       <div className="flex-1 min-w-0">
                         <p className="font-medium text-gray-900 capitalize">
                           {social.label || social.platform}
                         </p>
                         <p className="text-sm text-gray-500 truncate">
                           {social.url
                             .replace(/^https?:\/\/(www\.)?/, '')
                             .replace(/\/$/, '')}
                         </p>
                       </div>
                       <ExternalLink className="h-4 w-4 text-gray-400 group-hover:text-blue-600" />
                     </a>
                   ))}
                 </div>
               ) : (
                 <div className="text-center text-gray-500 mb-6">
                   <p>No social links added yet.</p>
                 </div>
               )}
 
               {/* Add/Edit Social Links (only for profile owner) */}
               {currentUser?.id === profile.id && (
                 <div className="mt-6 text-center">
                   <Link to="/dashboard/profile">
                     <Button
                       variant="outline"
                       className="border-blue-600 text-blue-600 hover:bg-blue-50"
                     >
                       <Link2 className="h-4 w-4 mr-2" />
                       Add / Edit Social Links
                     </Button>
                   </Link>
                 </div>
               )}
 
               {/* Contact Form Prompt (only for public visitors) */}
               {currentUser?.id !== profile.id && (
                 <div className="mt-8 text-center">
                   <p className="text-gray-600 mb-4">
                     Interested in working together or have a project in mind?
                   </p>
                   <Button size="lg" className="bg-blue-600 hover:bg-blue-700">
                     <Send className="h-4 w-4 mr-2" />
                     Send a Message
                   </Button>
                 </div>
               )}
             </CardContent>
           </Card>
         )}
         {/* {socialLinks.length > 0 && (
           <Card className="max-w-3xl mx-auto mb-12 shadow-lg border-0 rounded-2xl">
             <CardContent className="p-8">
               <h2 className="text-2xl font-bold text-center mb-6 text-gray-900">
                 Let's Connect
               </h2>
               <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                 {socialLinks.map((social, index) => (
                   <a
                     key={index}
                     href={social.url}
                     target="_blank"
                     rel="noopener noreferrer"
                     className="flex items-center gap-4 p-4 border border-gray-200 rounded-xl hover:bg-blue-50 hover:border-blue-300 transition-all group"
                   >
                     <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition-colors">
                       {getSocialIcon(social.platform)}
                     </div>
                     <div className="flex-1 min-w-0">
                       <p className="font-medium text-gray-900 capitalize">
                         {social.label || social.platform}
                       </p>
                       <p className="text-sm text-gray-500 truncate">
                         {social.url.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '')}
                       </p>
                     </div>
                     <ExternalLink className="h-4 w-4 text-gray-400 group-hover:text-blue-600" />
                   </a>
                 ))}
               </div>
 
             
               <div className="mt-8 text-center">
                 <p className="text-gray-600 mb-4">
                   Interested in working together or have a project in mind?
                 </p>
                 <Button size="lg" className="bg-blue-600 hover:bg-blue-700">
                   <Send className="h-4 w-4 mr-2" />
                   Send a Message
                 </Button>
               </div>
             </CardContent>
           </Card>
         )} */}
 
         {/* Footer */}
         <div className="max-w-5xl mx-auto py-8 text-center text-sm text-gray-500">
           <p>Portfolio powered by Innewgen </p>
         </div>
       </div>
 
       {/* Project Modal */}
       {selectedProject && (
         <ProjectModal 
           project={selectedProject} 
           onClose={() => {
             setSelectedProject(null);
             setCurrentMediaIndex(0);
           }} 
         />
       )}
     </div>
   );
 };
 
 export default PublicProfile;





//         const projectIdFromHash = window.location.hash.substring(1);
//         if (projectIdFromHash) {
//           const projectToOpen = projectsWithDetails.find(p => p.id === projectIdFromHash);
//           if (projectToOpen) {
//             setSelectedProject(projectToOpen);
//             setCurrentMediaIndex(0);
//           }
//         }
//       }

//     } catch (err) {
//       console.error('Error fetching profile:', err);
//       setError(err.message || 'Failed to load profile');
//       toast.error('Failed to load profile');
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   const handleShare = () => {
//     navigator.clipboard.writeText(window.location.href);
//     toast.success("Portfolio link copied to clipboard!");
//   };

//   const handleDownloadResume = () => {
//     if (profile?.resume_url) {
//       window.open(profile.resume_url, '_blank');
//       toast.success("Resume download started");
//     }
//   };

//   const getSocialIcon = (platform: string) => {
//     switch (platform.toLowerCase()) {
//       case 'github': return <Github className="h-5 w-5" />;
//       case 'linkedin': return <Linkedin className="h-5 w-5" />;
//       case 'facebook': return <Facebook className="h-5 w-5" />;
//       case 'instagram': return <Instagram className="h-5 w-5" />;
//       case 'twitter': return <Twitter className="h-5 w-5" />;
//       case 'dribbble': return <Dribbble className="h-5 w-5" />;
//       case 'youtube': return <Youtube className="h-5 w-5" />;
//       case 'portfolio': return <Globe className="h-5 w-5" />;
//       case 'medium': return <FileText className="h-5 w-5" />;
//       default: return <Globe className="h-5 w-5" />;
//     }
//   };

//   const getProjectIcon = (category: string) => {
//     const cat = category.toLowerCase();
//     if (cat.includes('video') || cat.includes('film')) return <Film className="h-4 w-4" />;
//     if (cat.includes('game')) return <Gamepad2 className="h-4 w-4" />;
//     if (cat.includes('web') || cat.includes('code')) return <Code className="h-4 w-4" />;
//     if (cat.includes('design') || cat.includes('graphic')) return <Palette className="h-4 w-4" />;
//     if (cat.includes('3d') || cat.includes('ar')) return <Box className="h-4 w-4" />;
//     return <FolderOpen className="h-4 w-4" />;
//   };

//   const getSkillLevel = (level: string): number => {
//     const levels: Record<string, number> = {
//       beginner: 1,
//       intermediate: 2,
//       advanced: 3,
//       expert: 4
//     };
//     return levels[level.toLowerCase()] || 2;
//   };

//   const getCoverMedia = (project: Project): ProjectMedia | null => {
//     return project.media.find(m => m.is_cover) || project.media[0] || null;
//   };

//   const categories = ['all', ...Array.from(new Set(projects.map(p => p.category)))];
//   const allTags = Array.from(new Set(projects.flatMap(p => p.tags)));

//   const filteredProjects = projects.filter(project => {
//     const categoryMatch = activeCategory === 'all' || project.category === activeCategory;
//     const tagMatch = activeTag === 'all' || project.tags.includes(activeTag);
//     return categoryMatch && tagMatch;
//   });

//   const featuredProjects = projects.filter(p => p.featured).slice(0, 3);
//   const skillsByCategory = skills.reduce((acc, skill) => {
//     if (!acc[skill.category]) acc[skill.category] = [];
//     acc[skill.category].push(skill);
//     return acc;
//   }, {} as Record<string, Skill[]>);

//   const ProjectModal = ({ project, onClose }: { project: Project; onClose: () => void }) => {
//     const media = project.media[currentMediaIndex];

//     return (
//       <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={onClose}>
//         <div className="bg-white rounded-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
//           <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between z-10">
//             <h3 className="text-2xl font-bold text-gray-900">{project.title}</h3>
//             <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
//               <X className="h-6 w-6" />
//             </button>
//           </div>

//           <div className="p-6">
//             {/* Media Gallery */}
//             {project.media.length > 0 && (
//               <div className="mb-6">
//                 <div className="relative bg-gray-900 rounded-xl overflow-hidden aspect-video">
//                   {media?.file_type === 'video' ? (
//                     <video 
//                       src={media.file_url} 
//                       controls 
//                       className="w-full h-full object-contain"
//                     />
//                   ) : (
//                     <img 
//                       src={media?.file_url} 
//                       alt={project.title}
//                       className="w-full h-full object-contain"
//                     />
//                   )}
                  
//                   {project.media.length > 1 && (
//                     <>
//                       <button
//                         onClick={() => setCurrentMediaIndex((currentMediaIndex - 1 + project.media.length) % project.media.length)}
//                         className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg"
//                       >
//                         <ChevronLeft className="h-6 w-6" />
//                       </button>
//                       <button
//                         onClick={() => setCurrentMediaIndex((currentMediaIndex + 1) % project.media.length)}
//                         className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg"
//                       >
//                         <ChevronRight className="h-6 w-6" />
//                       </button>
//                     </>
//                   )}
//                 </div>

//                 {project.media.length > 1 && (
//                   <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
//                     {project.media.map((m, i) => (
//                       <button
//                         key={m.id}
//                         onClick={() => setCurrentMediaIndex(i)}
//                         className={`relative flex-shrink-0 w-24 h-24 rounded-lg overflow-hidden ${
//                           i === currentMediaIndex ? 'ring-2 ring-blue-500' : ''
//                         }`}
//                       >
//                         <img 
//                           src={m.thumbnail_url || m.file_url} 
//                           alt=""
//                           className="w-full h-full object-cover"
//                         />
//                         {m.file_type === 'video' && (
//                           <Play className="absolute inset-0 m-auto h-6 w-6 text-white" />
//                         )}
//                       </button>
//                     ))}
//                   </div>
//                 )}
//               </div>
//             )}

//             {/* Project Details */}
//             <div className="space-y-6">
//               <div>
//                 <h4 className="text-sm font-semibold text-gray-500 mb-2">DESCRIPTION</h4>
//                 <p className="text-gray-700 leading-relaxed">{project.description}</p>
//               </div>

//               {/* Links */}
//               <div className="flex flex-wrap gap-3">
//                 {project.live_url && (
//                   <a href={project.live_url} target="_blank" rel="noopener noreferrer">
//                     <Button variant="default" className="bg-blue-600 hover:bg-blue-700">
//                       <ExternalLink className="h-4 w-4 mr-2" />
//                       View Live
//                     </Button>
//                   </a>
//                 )}
//                 {project.github_url && (
//                   <a href={project.github_url} target="_blank" rel="noopener noreferrer">
//                     <Button variant="outline">
//                       <Github className="h-4 w-4 mr-2" />
//                       GitHub
//                     </Button>
//                   </a>
//                 )}
//                 {project.youtube_url && (
//                   <a href={project.youtube_url} target="_blank" rel="noopener noreferrer">
//                     <Button variant="outline">
//                       <Youtube className="h-4 w-4 mr-2" />
//                       YouTube
//                     </Button>
//                   </a>
//                 )}
//                 {project.ar_model_url && (
//                   <a href={project.ar_model_url} target="_blank" rel="noopener noreferrer">
//                     <Button variant="outline">
//                       <Box className="h-4 w-4 mr-2" />
//                       View in AR
//                     </Button>
//                   </a>
//                 )}
//                 {project.case_study_url && (
//                   <a href={project.case_study_url} target="_blank" rel="noopener noreferrer">
//                     <Button variant="outline">
//                       <FileText className="h-4 w-4 mr-2" />
//                       Case Study
//                     </Button>
//                   </a>
//                 )}
//               </div>

//               {/* Tags */}
//               {project.tags.length > 0 && (
//                 <div>
//                   <h4 className="text-sm font-semibold text-gray-500 mb-2">TAGS</h4>
//                   <div className="flex flex-wrap gap-2">
//                     {project.tags.map((tag, i) => (
//                       <Badge key={i} variant="secondary" className="bg-blue-50 text-blue-700">
//                         {tag}
//                       </Badge>
//                     ))}
//                   </div>
//                 </div>
//               )}

//               {/* Collaborators */}
//               {project.collaborators.length > 0 && (
//                 <div>
//                   <h4 className="text-sm font-semibold text-gray-500 mb-3">COLLABORATORS</h4>
//                   <div className="space-y-2">
//                     {project.collaborators.map((collab, i) => (
//                       <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
//                         <Avatar className="h-10 w-10">
//                           <AvatarFallback>
//                             {collab.collaborator_name.split(' ').map(n => n[0]).join('')}
//                           </AvatarFallback>
//                         </Avatar>
//                         <div className="flex-1">
//                           {collab.collaborator_id ? (
//                             <Link to={`/profile/${collab.collaborator_id}`} className="font-medium text-blue-600 hover:underline">
//                               {collab.collaborator_name}
//                             </Link>
//                           ) : (
//                             <p className="font-medium text-gray-900">{collab.collaborator_name}</p>
//                           )}
//                           <p className="text-sm text-gray-500">{collab.role}</p>
//                         </div>
//                       </div>
//                     ))}
//                   </div>
//                 </div>
//               )}

//               {/* Stats */}
//               {/* <div className="flex items-center gap-6 text-sm text-gray-600">
//                 <div className="flex items-center gap-2">
//                   <Heart className="h-4 w-4 text-red-500" />
//                   {project.likes_count} likes
//                 </div>
//                 <div className="flex items-center gap-2">
//                   <Eye className="h-4 w-4" />
//                   {project.views_count} views
//                 </div>
            
//               </div> */}
//             </div>
//           </div>
//         </div>
//       </div>
//     );
//   };

//   if (isLoading) {
//     return (
//       <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center">
//         <div className="text-center">
//           <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
//           <p className="text-gray-600">Loading portfolio...</p>
//         </div>
//       </div>
//     );
//   }

//   if (error || !profile) {
//     return (
//       <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center">
//         <div className="text-center">
//           <h2 className="text-2xl font-bold mb-2">Profile Not Found</h2>
//           <p className="text-gray-600 mb-4">{error || 'The profile you are looking for does not exist.'}</p>
//           <Link to="/explore">
//             <Button>Back to Explore</Button>
//           </Link>
//         </div>
//       </div>
//     );
//   }

//   return (
//     <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
//       {/* Header */}
//       <header
//         className="bg-white/80 border-b sticky z-40 backdrop-blur-lg shadow-sm"
//         style={{ top: "var(--beta-banner-height)" }}
//       >
//         <div className="container mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
//           <Link to="/explore" className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 transition-colors">
//             <ArrowLeft className="h-4 w-4" />
//             Back to Explore
//           </Link>
//           <div className="flex items-center">
//         {currentUser?.id === profile.id && (
//            <Link to="/dashboard/profile">
//             <Button
//               variant="outline"
//               size="sm"
//               className="border-green-600 text-green-600 hover:bg-green-50 mr-4"
//             >
//               <Briefcase className="h-4 w-4 mr-2" />
//               Edit Profile
//             </Button>
//           </Link>
//         )}
//           <Button variant="outline" size="sm" onClick={handleShare} className="border-blue-600 text-blue-600 hover:bg-blue-50">
//             <Share2 className="h-4 w-4 mr-2" />
//             Share
//           </Button>
//           </div>
//         </div>
//       </header>

//       {/* Cover Image */}
//       {profile.cover_image_url && (
//         <div className="relative h-48 sm:h-64 lg:h-80 w-full overflow-hidden">
//           <img 
//             src={profile.cover_image_url} 
//             alt="Cover" 
//             className="w-full h-full object-cover"
//           />
//           <div className="absolute inset-0 bg-gradient-to-b from-transparent to-white/90" />
//         </div>
//       )}

//       <div className="container mx-auto px-4 sm:px-6 relative z-10 - mt-10">
//         {/* Profile Header */}
//          <Card className="max-w-6xl mx-auto mb-8 sm:mb-10 shadow-xl border border-primary-100 rounded-2xl bg-white">
//       <CardContent className="p-6 sm:p-8 lg:p-10">
//         <div className="flex flex-col items-center text-center gap-5 sm:gap-6">
//           {/* Avatar */}

//         <div className="relative inline-block">
//           <Avatar
//             className={`h-28 w-28 sm:h-32 sm:w-32 lg:h-40 lg:w-40 border-4 shadow-md rounded-full transition-colors duration-300 ${
//               profile.available_for_work ? "border-green-500" : "border-primary-500"
//             }`}
//           >
//             <AvatarImage
//               src={
//                 profile.avatar_url ||
//                 `https://api.dicebear.com/7.x/avataaars/svg?seed=${profile.username}`
//               }
//               className="object-cover"
//             />
//             <AvatarFallback>
//               {profile.full_name
//                 .split(" ")
//                 .map((n) => n[0])
//                 .join("")}
//             </AvatarFallback>
//           </Avatar>

//           {profile.available_for_work && (
//             <div className="absolute -bottom-3 left-1/2 transform -translate-x-1/2">
//               <Badge className="bg-green-300 text-success hover:bg-green-400 hover:text-green-800 border border-green-500 px-4 py-1 rounded-full whitespace-nowrap shadow-md">
//                 Available for Work
//               </Badge>
//             </div>
//           )}
//         </div>

//           {/* Info */}
//           <div className="flex flex-col items-center text-center gap-3 w-full">
//              <div className="flex flex-wrap items-center justify-center gap-2">
//               <h1 className="text-3xl font-bold text-gray-900">{profile.full_name}</h1>
//               <p className="text-primary-600 text-lg">@{profile.username}</p>
//             </div>

//             <Badge className="bg-primary/10 text-primary border border-primary/20">
//               {profile.program}
//             </Badge>

//             {profile.title && (
//               <p className="text-xl font-medium text-gray-800">{profile.title}</p>
//             )}

//             {profile.location && (
//               <div className="flex items-center justify-center gap-2 text-gray-600">
//                 <MapPin className="h-4 w-4 text-primary-500" />
//                 <span className="text-sm">{profile.location}</span>
//               </div>
//             )}

//             {profile.bio && (
//               <p className="text-gray-600 max-w-2xl leading-relaxed">{profile.bio}</p>
//             )}

//             {/* Action Buttons */}
//            <div className="flex flex-wrap justify-center gap-3 mt-4">
//             <Button className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-md w-full sm:w-auto">
//               <Mail className="h-4 w-4 mr-2" />
//               Contact Me
//             </Button>

//             {profile.resume_url && (
//               <Button
//                 variant="outline"
//                 className="border-primary text-primary hover:bg-primary hover:text-primary-foreground w-full sm:w-auto"
//               >
//                 <Download className="h-4 w-4 mr-2" />
//                 Download Resume
//               </Button>
//             )}
//           </div>

//           </div>
//         </div>
//       </CardContent>
//         </Card>
//         {/* About Section */}
//         {profile.about && (
//           <Card className="max-w-6xl mx-auto mb-12 shadow-lg border-0 rounded-2xl">
//             <CardContent className="p-8">
//               <h2 className="text-2xl font-bold mb-4 text-gray-900">About Me</h2>
//               <p className="text-gray-700 leading-relaxed whitespace-pre-line">{profile.about}</p>
//             </CardContent>
//           </Card>
//         )}

        
//         {/* Skills Section */}
//         {/* {skills.length > 0 && (
//           <div className="max-w-4xl mx-auto mb-10">
//             <h2 className="text-2xl font-bold mb-4 text-gray-900">Skills & Tools</h2>
//             <Card className="shadow-lg border border-primary-100 rounded-2xl">
//               <CardContent className="p-6">
//                 <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
//                   {skills.map((skill, index) => {
//                     const level = getSkillLevel(skill.level);
//                     return (
//                       <div key={index} className="space-y-2">
//                         <div className="flex items-center justify-between">
//                           <span className="font-medium text-gray-800">{skill.name}</span>
//                           <span className="text-sm text-gray-500">{level}/4</span>
//                         </div>
//                         <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
//                           <div
//                             className="h-full bg-primary-500 transition-all rounded-full"
//                             style={{ width: `${(level / 4) * 100}%` }}
//                           />
//                         </div>
//                       </div>
//                     );
//                   })}
//                 </div>
//               </CardContent>
//             </Card>
//           </div>
//         )} */}


//         {/* Featured Projects */}
//         {featuredProjects.length > 0 && (
//           <div className="max-w-5xl mx-auto mb-12">
//             <h2 className="text-2xl font-bold mb-6 text-gray-900">Featured Work</h2>
//             <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
//               {featuredProjects.map(project => {
//                 const coverMedia = getCoverMedia(project);
//                 return (
//                   <Card 
//                     key={project.id}
//                     className="group cursor-pointer hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 overflow-hidden border-0 rounded-xl"
//                     onClick={() => {
//                       setSelectedProject(project);
//                       setCurrentMediaIndex(0);
//                     }}
//                   >
//                     <div className="relative h-48 bg-gray-900 overflow-hidden">
//                       {coverMedia && (
//                         <>
//                           {coverMedia.file_type === 'video' ? (
//                             <video src={coverMedia.file_url} className="w-full h-full object-cover" />
//                           ) : (
//                             <img src={coverMedia.file_url} alt={project.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300" />
//                           )}
//                         </>
//                       )}
//                       <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
//                       <Badge className="absolute top-3 right-3 bg-yellow-500 text-white border-0">
//                         Featured
//                       </Badge>
//                     </div>
//                     <CardContent className="p-4">
//                       <h3 className="font-semibold text-lg mb-2 text-gray-900">{project.title}</h3>
//                       <p className="text-sm text-gray-600 line-clamp-2">{project.description}</p>
//                       {/* <div className="flex items-center justify-between mt-3 text-sm text-gray-500">
//                         <div className="flex items-center gap-1">
//                           <Heart className="h-4 w-4 text-red-500" />
//                           {project.likes_count}
//                         </div>
//                         <div className="flex items-center gap-1">
//                           <Eye className="h-4 w-4" />
//                           {project.views_count}
//                         </div>
//                       </div> */}
//                     </CardContent>
//                   </Card>
//                 );
//               })}
//             </div>
//           </div>
//         )}

//         {/* Skills Section */}
//         {Object.keys(skillsByCategory).length > 0 && (
//           <Card className="max-w-5xl mx-auto mb-12 shadow-lg border-0 rounded-2xl">
//             <CardContent className="p-8">
//               <h2 className="text-2xl font-bold mb-6 text-gray-900">Skills & Expertise</h2>
//               <div className="space-y-6">
//                 {Object.entries(skillsByCategory).map(([category, categorySkills]) => (
//                   <div key={category}>
//                     <h3 className="text-sm font-semibold text-gray-500 mb-3 uppercase">{category}</h3>
//                     <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                       {categorySkills.map((skill, index) => {
//                         const level = getSkillLevel(skill.level);
//                         return (
//                           <div key={index} className="space-y-2">
//                             <div className="flex items-center justify-between">
//                               <span className="font-medium text-gray-800">{skill.name}</span>
//                               <span className="text-sm text-gray-500 capitalize">{skill.level}</span>
//                             </div>
//                             <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
//                               <div
//                                 className="h-full bg-blue-600 transition-all rounded-full"
//                                 style={{ width: `${(level / 4) * 100}%` }}
//                               />
//                             </div>
//                           </div>
//                         );
//                       })}
//                     </div>
//                   </div>
//                 ))}
//               </div>
//             </CardContent>
//           </Card>
//         )}

//         {/* Projects Section */}
//         {projects.length > 0 && (
//           <div className="max-w-6xl mx-auto mb-12">
//             <h2 className="text-2xl font-bold mb-6 text-gray-900">All Projects</h2>

//             {/* Category Filter */}
//             <div className="flex flex-wrap gap-2 mb-4">
//               {categories.map(cat => (
//                 <Badge
//                   key={cat}
//                   onClick={() => setActiveCategory(cat)}
//                   className={`cursor-pointer px-4 py-2 ${
//                     activeCategory === cat
//                       ? 'bg-blue-600 text-white hover:bg-blue-700'
//                       : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
//                   }`}
//                 >
//                   {cat === 'all' ? 'All' : cat}
//                 </Badge>
//               ))}
//             </div>

//             {/* Tag Filter */}
//             {allTags.length > 0 && (
//               <div className="flex flex-wrap gap-2 mb-6">
//                 <Badge
//                   onClick={() => setActiveTag('all')}
//                   className={`cursor-pointer ${
//                     activeTag === 'all'
//                       ? 'bg-blue-100 text-blue-700'
//                       : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
//                   }`}
//                 >
//                   All Tags
//                 </Badge>
//                 {allTags.map(tag => (
//                   <Badge
//                     key={tag}
//                     onClick={() => setActiveTag(tag)}
//                     className={`cursor-pointer ${
//                       activeTag === tag
//                         ? 'bg-blue-100 text-blue-700'
//                         : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
//                     }`}
//                   >
//                     {tag}
//                   </Badge>
//                 ))}
//               </div>
//             )}

//             {/* Projects Grid */}
// <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
//   {filteredProjects.map(project => {
//     const coverMedia = getCoverMedia(project);
//     return (
//       <Card
//         key={project.id}
//         className="group cursor-pointer hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden border-0 rounded-xl"
//         onClick={() => {
//           setSelectedProject(project);
//           setCurrentMediaIndex(0);
//         }}
//       >
//         <div className="relative h-56 bg-gray-900 overflow-hidden">
//           {coverMedia ? (
//             <>
//               {coverMedia.file_type === 'video' ? (
//                 <>
//                   <video src={coverMedia.file_url} className="w-full h-full object-cover" />
//                   <div className="absolute inset-0 flex items-center justify-center bg-black/30">
//                     <Play className="h-12 w-12 text-white" />
//                   </div>
//                 </>
//               ) : (
//                 <img
//                   src={coverMedia.file_url}
//                   alt={project.title}
//                   className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
//                 />
//               )}
//             </>
//           ) : (
//             <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-500 to-purple-600">
//               {getProjectIcon(project.category)}
//             </div>
//           )}
//           <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />

//           {/* Project Type Badge */}
//           <Badge className="absolute top-3 left-3 bg-white/90 text-gray-900 border-0">
//             <span className="mr-1">{getProjectIcon(project.category)}</span>
//             {project.category}
//           </Badge>

//           {/* Edit button for profile owner */}
//           {currentUser?.id === profile.id && (
//             <Link
//               to={`/dashboard/projects/edit/${project.id}`}
//               onClick={(e) => e.stopPropagation()} // Prevent modal from opening
//               className="absolute top-3 right-3 z-10"
//             >
//               <Button
//                 variant="secondary"
//                 size="sm"
//                 className="h-8 w-8 p-0"
//               >
//                 <Edit className="h-4 w-4" />
//                 <span className="sr-only">Edit Project</span>
//               </Button>
//             </Link>
//           )}

//           {project.featured && (
//             <Badge className="absolute top-3 right-3 bg-yellow-500 text-white border-0">
//               Featured
//             </Badge>
//           )}

//           {/* Media Count */}
//           {project.media.length > 1 && (
//             <div className="absolute bottom-3 right-3 bg-black/70 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
//               <ImageIcon className="h-3 w-3" />
//               {project.media.length}
//             </div>
//           )}
//         </div>

//         <CardContent className="p-5">
//           <h3 className="font-semibold text-lg mb-2 text-gray-900 line-clamp-1">
//             {project.title}
//           </h3>
//           <p className="text-sm text-gray-600 line-clamp-2 mb-3">
//             {project.description}
//           </p>

//           {/* Tags */}
//           {project.tags.length > 0 && (
//             <div className="flex flex-wrap gap-1 mb-3">
//               {project.tags.slice(0, 3).map((tag, i) => (
//                 <Badge key={i} variant="secondary" className="text-xs bg-blue-50 text-blue-700">
//                   {tag}
//                 </Badge>
//               ))}
//               {project.tags.length > 3 && (
//                 <Badge variant="secondary" className="text-xs bg-gray-100 text-gray-600">
//                   +{project.tags.length - 3}
//                 </Badge>
//               )}
//             </div>
//           )}

//           {/* Collaborators Preview */}
//           {project.collaborators.length > 0 && (
//             <div className="flex items-center gap-2 mb-3 text-sm text-gray-600">
//               <Users className="h-4 w-4" />
//               <span>
//                 {project.collaborators.length} collaborator{project.collaborators.length > 1 ? 's' : ''}
//               </span>
//             </div>
//           )}

//           {/* Stats & Links */}
//           <div className="flex items-center justify-between pt-3 border-t">
//             <div className="flex items-center gap-3 text-sm text-gray-500">
//               <div className="flex items-center gap-1">
//                 <Eye className="h-4 w-4" />
//                 {project.views_count}
//               </div>
//             </div>

//             <div className="flex items-center gap-2">
//               {project.live_url && <ExternalLink className="h-4 w-4 text-blue-600" />}
//               {project.github_url && <Github className="h-4 w-4 text-gray-700" />}
//               {project.youtube_url && <Youtube className="h-4 w-4 text-red-600" />}
//             </div>
//           </div>
//         </CardContent>
//       </Card>
//     );
//   })}

//   {/* Add Project Card - Only show if currentUser owns this profile */}
//   {currentUser?.id === profile.id && (
//     <Link
//       to="/dashboard/projects/new"
//       className="flex flex-col items-center justify-center cursor-pointer border-2 border-dashed border-gray-300 rounded-xl p-8 text-gray-400 hover:border-blue-500 hover:bg-blue-50 hover:text-blue-600 transition-all"
//     >
//       <Card className="flex flex-col items-center justify-center border-0 shadow-none">
//         <div className="flex items-center justify-center w-16 h-16 rounded-full border-2 border-gray-300 mb-4 group-hover:border-blue-500 group-hover:text-blue-600">
//           <Plus className="w-8 h-8" />
//         </div>
//         <span className="font-semibold text-lg select-none">Add Project</span>
//       </Card>
//     </Link>
//   )}

// </div>


//             {filteredProjects.length === 0 && (
//               <div className="text-center py-12">
//                 <FolderOpen className="h-12 w-12 text-gray-400 mx-auto mb-3" />
//                 <p className="text-gray-500">No projects found with selected filters</p>
//               </div>
//             )}
//           </div>
//         )}

//         {/* Connect Section */}
//                 {(socialLinks.length > 0 || currentUser?.id === profile.id) && (
//           <Card className="max-w-3xl mx-auto mb-12 shadow-lg border-0 rounded-2xl">
//             <CardContent className="p-8">
//               <h2 className="text-2xl font-bold text-center mb-6 text-gray-900">
//                 Let's Connect
//               </h2>

//               {/* Social Links Grid */}
//               {socialLinks.length > 0 ? (
//                 <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
//                   {socialLinks.map((social, index) => (
//                     <a
//                       key={index}
//                       href={social.url}
//                       target="_blank"
//                       rel="noopener noreferrer"
//                       className="flex items-center gap-4 p-4 border border-gray-200 rounded-xl hover:bg-blue-50 hover:border-blue-300 transition-all group"
//                     >
//                       <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition-colors">
//                         {getSocialIcon(social.platform)}
//                       </div>
//                       <div className="flex-1 min-w-0">
//                         <p className="font-medium text-gray-900 capitalize">
//                           {social.label || social.platform}
//                         </p>
//                         <p className="text-sm text-gray-500 truncate">
//                           {social.url
//                             .replace(/^https?:\/\/(www\.)?/, '')
//                             .replace(/\/$/, '')}
//                         </p>
//                       </div>
//                       <ExternalLink className="h-4 w-4 text-gray-400 group-hover:text-blue-600" />
//                     </a>
//                   ))}
//                 </div>
//               ) : (
//                 <div className="text-center text-gray-500 mb-6">
//                   <p>No social links added yet.</p>
//                 </div>
//               )}

//               {/* Add/Edit Social Links (only for profile owner) */}
//               {currentUser?.id === profile.id && (
//                 <div className="mt-6 text-center">
//                   <Link to="/dashboard/profile">
//                     <Button
//                       variant="outline"
//                       className="border-blue-600 text-blue-600 hover:bg-blue-50"
//                     >
//                       <Link2 className="h-4 w-4 mr-2" />
//                       Add / Edit Social Links
//                     </Button>
//                   </Link>
//                 </div>
//               )}

//               {/* Contact Form Prompt (only for public visitors) */}
//               {currentUser?.id !== profile.id && (
//                 <div className="mt-8 text-center">
//                   <p className="text-gray-600 mb-4">
//                     Interested in working together or have a project in mind?
//                   </p>
//                   <Button size="lg" className="bg-blue-600 hover:bg-blue-700">
//                     <Send className="h-4 w-4 mr-2" />
//                     Send a Message
//                   </Button>
//                 </div>
//               )}
//             </CardContent>
//           </Card>
//         )}
//         {/* {socialLinks.length > 0 && (
//           <Card className="max-w-3xl mx-auto mb-12 shadow-lg border-0 rounded-2xl">
//             <CardContent className="p-8">
//               <h2 className="text-2xl font-bold text-center mb-6 text-gray-900">
//                 Let's Connect
//               </h2>
//               <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
//                 {socialLinks.map((social, index) => (
//                   <a
//                     key={index}
//                     href={social.url}
//                     target="_blank"
//                     rel="noopener noreferrer"
//                     className="flex items-center gap-4 p-4 border border-gray-200 rounded-xl hover:bg-blue-50 hover:border-blue-300 transition-all group"
//                   >
//                     <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition-colors">
//                       {getSocialIcon(social.platform)}
//                     </div>
//                     <div className="flex-1 min-w-0">
//                       <p className="font-medium text-gray-900 capitalize">
//                         {social.label || social.platform}
//                       </p>
//                       <p className="text-sm text-gray-500 truncate">
//                         {social.url.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '')}
//                       </p>
//                     </div>
//                     <ExternalLink className="h-4 w-4 text-gray-400 group-hover:text-blue-600" />
//                   </a>
//                 ))}
//               </div>

            
//               <div className="mt-8 text-center">
//                 <p className="text-gray-600 mb-4">
//                   Interested in working together or have a project in mind?
//                 </p>
//                 <Button size="lg" className="bg-blue-600 hover:bg-blue-700">
//                   <Send className="h-4 w-4 mr-2" />
//                   Send a Message
//                 </Button>
//               </div>
//             </CardContent>
//           </Card>
//         )} */}

//         {/* Footer */}
//         <div className="max-w-5xl mx-auto py-8 text-center text-sm text-gray-500">
//           <p>Portfolio powered by Innewgen </p>
//         </div>
//       </div>

//       {/* Project Modal */}
//       {selectedProject && (
//         <ProjectModal 
//           project={selectedProject} 
//           onClose={() => {
//             setSelectedProject(null);
//             setCurrentMediaIndex(0);
//           }} 
//         />
//       )}
//     </div>
//   );
// };

// export default PublicProfile;